import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { MobileCenteredLayout } from '@/components/MobileCenteredLayout/MobileCenteredLayout';
import { INVITE_NAMESPACE } from '@/i18n';
import { CompanionsSection } from '@/Pages/Companions/components/CompanionsSection';
import { useInvite } from '@/Pages/Invite/hooks/useInvite';

/**
 * Tela única do convite/grupo (`/companions` não existe mais como rota).
 * O link é pessoal, então ela CONFIRMA quem é ("Você é Fulano?") e segue para a
 * selfie; depois da selfie do titular vira a tela do grupo: banner de "facial
 * pronta" + cadastro dos acompanhantes logo abaixo.
 */
export function InvitePage(): JSX.Element {
  const { t } = useTranslation(INVITE_NAMESPACE);
  const { screen, invite, role, confirmation, titularName, isSubmitting, confirm, reject } =
    useInvite();

  return (
    <MobileCenteredLayout>
      <Box
        component="main"
        data-testid="invite-page-main"
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, px: 3, py: 4 }}
      >
        {screen === 'loading' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CircularProgress size={20} />
            <Typography component="p" variant="body2">
              {t('loading')}
            </Typography>
          </Box>
        )}

        {screen === 'invalid' && (
          <>
            <Typography component="h1" variant="h5">
              {t('invalidTitle')}
            </Typography>
            <Alert severity="error" data-testid="invite-invalid">
              {t('invalidMessage')}
            </Alert>
          </>
        )}

        {/* Confirmação de identidade. O link é PESSOAL: não há CPF a pedir nem
            papel a perguntar — só confirmar quem é. Sem nome no convite (vaga
            de acompanhante, convidado importado sem nome) a pergunta vira
            "você é um convidado de <titular ou evento>?". */}
        {screen === 'confirm' && invite !== null && (
          <>
            <Box component="header">
              <Typography component="h1" variant="h5" data-testid="invite-confirm-question">
                {confirmation.kind === 'named'
                  ? t('confirmName', { name: confirmation.name })
                  : confirmation.kind === 'guestOf'
                    ? t('confirmGuestOf', { host: confirmation.host })
                    : t('confirmGeneric')}
              </Typography>
              <Typography component="p" variant="body2" color="text.secondary">
                {t('confirmSubtitle')}
              </Typography>
              {role === 'companion' && (
                <Typography component="p" variant="body2" sx={{ mt: 1 }}>
                  {t('confirmCompanionHint')}
                </Typography>
              )}
              {role === 'titular' && invite.companions.declared > 0 && (
                <Typography component="p" variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
                  {t('companionsProgress', {
                    captured: invite.companions.captured,
                    declared: invite.companions.declared,
                  })}
                </Typography>
              )}
            </Box>

            {invite.titularOnboarded && role === 'titular' && (
              <Alert severity="info" data-testid="invite-titular-done">
                {invite.companions.declared > 0 && invite.companions.pending === 0
                  ? t('allDoneMessage')
                  : t('titularDone')}
              </Alert>
            )}

            <Button
              variant="contained"
              size="large"
              disabled={isSubmitting}
              onClick={() => void confirm()}
              data-testid="invite-confirm"
            >
              {t('confirmCta')}
            </Button>

            <Button
              variant="text"
              size="large"
              disabled={isSubmitting}
              onClick={reject}
              data-testid="invite-reject"
            >
              {t('rejectCta')}
            </Button>
          </>
        )}

        {screen === 'group' && (
          <>
            <Typography component="h1" variant="h5">
              {t('groupTitle')}
            </Typography>
            <Alert severity="success" data-testid="invite-biometrics-ready">
              {titularName === ''
                ? t('biometricsReadyNoName')
                : t('biometricsReady', { name: titularName })}
            </Alert>
            <Divider />
            <CompanionsSection />
          </>
        )}
      </Box>
    </MobileCenteredLayout>
  );
}
